import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { API_URL } from '../App';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignUp = location.pathname === '/signup';

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  const set = e => { setError(''); setForm(f => ({ ...f, [e.target.name]: e.target.value })); };

  const storeAndRedirect = (data) => {
    localStorage.setItem('applyai_token', data.token);
    localStorage.setItem('applyai_user', JSON.stringify(data.user));
    navigate('/dashboard');
  };

  // Email / password submit
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const endpoint = isSignUp ? '/api/auth/register' : '/api/auth/login';
      const body = isSignUp ? form : { email: form.email, password: form.password };
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); return; }
      storeAndRedirect(data);
    } catch {
      setError('Could not connect to the server. Is the backend running?');
    } finally {
      setLoading(false);
    }
  };

  // Google sign-in success (receives the ID token credential from Google)
  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Google sign-in failed.'); return; }
      storeAndRedirect(data);
    } catch {
      setError('Could not connect to the server. Is the backend running?');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      {/* Left panel — branding */}
      <div className="auth-left">
        <div className="auth-left-inner">
          <Link to="/" className="auth-brand">
            <span className="logo-badge">AI</span>
            <span className="auth-brand-name">ApplyAI</span>
          </Link>
          <div className="auth-left-copy">
            <h2>Your job search,<br />finally organised.</h2>
            <p>Track applications, write AI-powered emails, and manage recruiter relationships — all in one place.</p>
          </div>
          <div className="auth-features-list">
            {[
              ['✦', 'Smart application tracker with status pipeline'],
              ['✦', 'AI email assistant trained on your resume'],
              ['✦', 'Contact manager with outreach tracking'],
              ['✦', 'One-click Chrome extension (coming soon)'],
            ].map(([icon, text]) => (
              <div key={text} className="auth-feature-item">
                <span className="auth-feature-dot">{icon}</span>
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="auth-right">
        <div className="auth-card">
          {/* Mobile logo */}
          <Link to="/" className="auth-brand auth-brand-mobile">
            <span className="logo-badge">AI</span>
            <span className="auth-brand-name">ApplyAI</span>
          </Link>

          <div className="auth-heading">
            <h1>{isSignUp ? 'Create your account' : 'Welcome back'}</h1>
            <p>
              {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
              <Link to={isSignUp ? '/login' : '/signup'} className="auth-link">
                {isSignUp ? 'Sign in' : 'Sign up for free'}
              </Link>
            </p>
          </div>

          {/* Google Sign-In */}
          <div className="auth-google-wrap">
            {googleLoading && (
              <div className="auth-google-loading">
                <span className="auth-spinner" style={{ borderColor: 'rgba(0,0,0,0.15)', borderTopColor: '#6366f1' }} />
                <span style={{ fontSize: '0.88rem', color: '#64748b' }}>Signing in with Google…</span>
              </div>
            )}
            <div style={{ display: googleLoading ? 'none' : 'block' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google sign-in was cancelled or failed. Please try again.')}
                theme="outline"
                size="large"
                text={isSignUp ? 'signup_with' : 'signin_with'}
                shape="rectangular"
                logo_alignment="left"
              />
            </div>
          </div>

          <div className="auth-divider"><span>or continue with email</span></div>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={submit} className="auth-form">
            {isSignUp && (
              <div className="auth-field">
                <label>Full name</label>
                <input
                  name="name" value={form.name} onChange={set}
                  placeholder="Jane Smith" autoComplete="name"
                />
              </div>
            )}
            <div className="auth-field">
              <label>Email address</label>
              <input
                name="email" type="email" value={form.email} onChange={set}
                placeholder="you@example.com" required autoComplete="email"
              />
            </div>
            <div className="auth-field">
              <label>
                Password
                {!isSignUp && (
                  <button type="button" className="auth-forgot"
                    onClick={() => setError('Password reset coming soon.')}>
                    Forgot password?
                  </button>
                )}
              </label>
              <div className="auth-pass-wrap">
                <input
                  name="password" type={showPass ? 'text' : 'password'}
                  value={form.password} onChange={set}
                  placeholder={isSignUp ? 'Min 8 characters' : '••••••••'}
                  required
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                <button type="button" className="auth-pass-toggle"
                  onClick={() => setShowPass(v => !v)}>
                  {showPass ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading || googleLoading}>
              {loading
                ? <span className="auth-spinner" />
                : isSignUp ? 'Create account' : 'Sign in'
              }
            </button>
          </form>

          <p className="auth-terms">
            By continuing you agree to our{' '}
            <span className="auth-link" style={{ cursor: 'default' }}>Terms of Service</span>
            {' '}and{' '}
            <span className="auth-link" style={{ cursor: 'default' }}>Privacy Policy</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
